import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { api } from "./api";

/*
 * Web push for the installed PWA (PLAN.md §Notifications). Permission is never
 * asked on first launch: only from askForPush() at a moment where it's clearly
 * useful, or from Settings. Saying no changes nothing else; the inbox works.
 */

export type PushSupport = "ok" | "ios-install" | "unsupported";
export type PushState = "on" | "off" | "denied" | "ios-install" | "unsupported" | "unavailable";

const isIOS = () => /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
const standalone = () => window.matchMedia?.("(display-mode: standalone)").matches || (navigator as { standalone?: boolean }).standalone === true;

/** iOS only delivers web push to an app added to the home screen (iOS 16.4+). */
export function pushSupport(): PushSupport {
  if (isIOS() && !standalone()) return "ios-install";
  if (!("serviceWorker" in navigator) || !("PushManager" in window) || !("Notification" in window)) return "unsupported";
  return "ok";
}

let registration: Promise<ServiceWorkerRegistration | null> | null = null;
export function serviceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!("serviceWorker" in navigator)) return Promise.resolve(null);
  registration ??= navigator.serviceWorker.register("/sw.js").then(() => navigator.serviceWorker.ready).catch(() => null);
  return registration;
}

let publicKey: Promise<string | null> | null = null;
const vapidKey = () => (publicKey ??= api.pushKey().then((r) => r.key).catch(() => null));

const keyBytes = (b64: string) => {
  const raw = atob(b64.replace(/-/g, "+").replace(/_/g, "/") + "=".repeat((4 - (b64.length % 4)) % 4));
  return Uint8Array.from(raw, (ch) => ch.charCodeAt(0));
};

export async function pushState(): Promise<PushState> {
  const support = pushSupport();
  if (support !== "ok") return support;
  if (!(await vapidKey())) return "unavailable";
  if (Notification.permission === "denied") return "denied";
  const reg = await serviceWorker();
  const sub = await reg?.pushManager.getSubscription();
  return sub && Notification.permission === "granted" ? "on" : "off";
}

/** Asks the browser (this is the permission prompt), then registers this device. */
export async function enablePush(): Promise<PushState> {
  const key = await vapidKey();
  if (pushSupport() !== "ok" || !key) return pushState();
  const perm = await Notification.requestPermission();
  if (perm !== "granted") return perm === "denied" ? "denied" : "off";
  const reg = await serviceWorker();
  if (!reg) return "unsupported";
  const sub = (await reg.pushManager.getSubscription()) ?? (await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: keyBytes(key) }));
  const j = sub.toJSON() as { endpoint: string; keys: { p256dh: string; auth: string } };
  await api.addPushSubscription({ endpoint: j.endpoint, keys: j.keys });
  return "on";
}

/** This device only; other devices keep theirs. */
export async function disablePush(): Promise<PushState> {
  const reg = await serviceWorker();
  const sub = await reg?.pushManager.getSubscription();
  if (sub) {
    await api.removePushSubscription(sub.endpoint).catch(() => {});
    await sub.unsubscribe().catch(() => false);
  }
  return pushState();
}

/** The bell's number, kept fresh, and mirrored on the app icon where the Badging API exists. */
export function useUnread(): number {
  const q = useQuery({ queryKey: ["notif-count"], queryFn: api.notificationCount, refetchInterval: 60_000, refetchOnWindowFocus: true, staleTime: 15_000 });
  const n = q.data?.unread ?? 0;
  useEffect(() => {
    const nav = navigator as Navigator & { setAppBadge?: (n: number) => Promise<void>; clearAppBadge?: () => Promise<void> };
    if (!q.data || !nav.setAppBadge) return;
    void (n > 0 ? nav.setAppBadge(n) : nav.clearAppBadge?.())?.catch(() => {});
  }, [n, q.data]);
  return n;
}

/**
 * Once per signed-in session: tell the server the browser's time zone (it only
 * fills one that isn't known), re-send this device's subscription so a rotated
 * endpoint is never lost, and route taps on notifications inside the open app.
 */
export function useNotificationBoot() {
  const navigate = useNavigate();
  useEffect(() => {
    try {
      if (!sessionStorage.getItem("pm_tz_sent")) {
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        if (tz) void api.rememberZone(tz).then(() => sessionStorage.setItem("pm_tz_sent", "1")).catch(() => {});
      }
    } catch { /* storage blocked: the zone is sent next time */ }
    if (pushSupport() !== "ok" || Notification.permission !== "granted") return;
    void serviceWorker().then(async (reg) => {
      const sub = await reg?.pushManager.getSubscription();
      const j = sub?.toJSON() as { endpoint?: string; keys?: { p256dh: string; auth: string } } | undefined;
      if (j?.endpoint && j.keys) await api.addPushSubscription({ endpoint: j.endpoint, keys: j.keys }).catch(() => {});
    });
  }, []);
  useEffect(() => {
    if (!("serviceWorker" in navigator)) return;
    const onMessage = (e: MessageEvent) => {
      if (e.data?.type === "pm:navigate" && typeof e.data.url === "string") void navigate({ href: e.data.url });
    };
    navigator.serviceWorker.addEventListener("message", onMessage);
    return () => navigator.serviceWorker.removeEventListener("message", onMessage);
  }, [navigate]);
}

/** This browser's push endpoint, if it has one: lets the device list say "this device". */
export async function currentEndpoint(): Promise<string | null> {
  if (pushSupport() !== "ok") return null;
  const reg = await serviceWorker();
  const sub = await reg?.pushManager.getSubscription();
  return sub?.endpoint ?? null;
}
